/**
 * 回前景重載（client only）：已登入的頁面在背景待超過 token 效期、或回前景後發現換了帳號，
 * 就在既有的自動登入／回前景探測流程結束後整頁重載一次，讓頁面狀態不比 token 舊。
 * 規格見 spec/2026-10-19-reward-claim-identity-scope/design.md §5.5（REQ-16～19）。
 *
 * 自動登入流程（useAutoLogin / reviveSession / runAutoLogin）完全不動：本 plugin 只等它們的旗標放下。
 */

import { nextTick, watch } from 'vue'

import { TOKEN_SECONDS } from '@/composables/auth/useAuthToken'
import { useAuthStore } from '@/stores/auth/auth'
import { useIdentityScopeStore } from '@/stores/auth/identityScope'

// 超過 token cookie 效期必然已經過一次自動登入，頁面上的狀態都比 token 舊（BR-6）
const RELOAD_AFTER_HIDDEN_MS = TOKEN_SECONDS * 1000

export default defineNuxtPlugin(() => {
  const authStore = useAuthStore()
  const identityScope = useIdentityScopeStore()
  const { isLoggedIn } = useAuthToken()
  const { saveCredentialCookie } = useAutoLogin()

  let hiddenAt = null
  let uidSiteAtHidden = null
  let stopWaitingForSettle = null

  const cancelPendingDecision = () => {
    stopWaitingForSettle?.()
    stopWaitingForSettle = null
  }

  // 退背景時 useAuth 會把 isBackgroundEpisode 設起來，回前景由 reviveSession / runAutoLogin 放下
  const isSettled = () => !authStore.isAutoLoggingIn && !authStore.isRevivingSession && !authStore.isBackgroundEpisode

  const shouldReload = ({ hiddenFor, uidSiteBefore }) => {
    // 自動登入失敗已登出時保留錯誤彈窗；離線時重載只會得到瀏覽器的錯誤頁
    if (!isLoggedIn.value || document.visibilityState !== 'visible' || navigator.onLine === false) return false
    if (uidSiteBefore && identityScope.uidSite !== uidSiteBefore) return true
    if (authStore.isPlaying) return false
    return hiddenFor >= RELOAD_AFTER_HIDDEN_MS
  }

  const reload = async () => {
    // 自動登入成功後 cred cookie 已被清掉、只剩記憶體密文；不寫回的話重載後下一次背景過期就救不回（REQ-17）
    saveCredentialCookie()
    // useCookie 在 watcher flush 時才寫入 document.cookie
    await nextTick()
    window.location.reload()
  }

  const markHidden = () => {
    cancelPendingDecision()
    // 一段背景期只記第一次（visibilitychange 與 pagehide 可能都會觸發）
    if (hiddenAt !== null) return
    hiddenAt = Date.now()
    uidSiteAtHidden = identityScope.uidSite
  }

  const markVisible = () => {
    if (hiddenAt === null) return
    const context = { hiddenFor: Date.now() - hiddenAt, uidSiteBefore: uidSiteAtHidden }
    hiddenAt = null
    uidSiteAtHidden = null
    cancelPendingDecision()

    const decide = () => {
      if (shouldReload(context)) reload()
    }
    if (isSettled()) {
      decide()
      return
    }
    stopWaitingForSettle = watch(isSettled, (settled) => {
      if (!settled) return
      cancelPendingDecision()
      decide()
    })
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') markHidden()
    else if (document.visibilityState === 'visible') markVisible()
  }
  const handlePageShow = (event) => {
    if (event.persisted) markVisible()
  }

  // 須早於 useAuth 在登入後才註冊的 visibilitychange 監聽：回前景時本 plugin 先記下背景時長，
  // 此時 isBackgroundEpisode 仍為 true，一定會等到既有流程跑完
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pagehide', markHidden)
  window.addEventListener('pageshow', handlePageShow)

  // client plugin 與整個 app 同生命週期；dev HMR 重載本檔時才需要清理
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      cancelPendingDecision()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', markHidden)
      window.removeEventListener('pageshow', handlePageShow)
    })
  }
})
